import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

/**
 * Loads and caches KayKit Dungeon Pack models by name (no extension) — dedupes
 * concurrent requests for the same model so a layout with many repeated
 * prefabs (design/gdd/procedural-layout-assembly.md Edge Case #7) only
 * downloads/parses each .gltf once.
 */

const ASSET_BASE = '/assets/dungeon-pack/models';
const PALETTE_BASE = '/assets/dungeon-pack/textures/palette-swaps';
const loader = new GLTFLoader();
const textureLoader = new THREE.TextureLoader();
const cache = new Map<string, Promise<THREE.Group>>();
const paletteCache = new Map<string, Promise<THREE.Texture>>();

export function loadModel(name: string): Promise<THREE.Group> {
  let pending = cache.get(name);
  if (!pending) {
    pending = new Promise((resolve, reject) => {
      loader.load(
        `${ASSET_BASE}/${name}.gltf`,
        (gltf) => resolve(gltf.scene),
        undefined,
        (err) => reject(new Error(`Failed to load model "${name}": ${String(err)}`)),
      );
    });
    cache.set(name, pending);
  }
  return pending;
}

/**
 * design/gdd/game-concept.md §10 Risk #3 ("the KayKit look is generic") —
 * the pack's own hand-authored recolored atlases
 * (`textures/palette-swaps/*.png`, same UV layout as `dungeon_texture.png`),
 * loaded and cached by filename (no extension).
 */
export function loadPaletteTexture(fileName: string): Promise<THREE.Texture> {
  let pending = paletteCache.get(fileName);
  if (!pending) {
    pending = new Promise((resolve, reject) => {
      textureLoader.load(
        `${PALETTE_BASE}/${fileName}.png`,
        (texture) => {
          // Matches the colorSpace/flipY GLTFLoader itself sets on the
          // model's own embedded base-color texture (same reasoning as
          // character-pack-cache.ts's loadSkinTexture).
          texture.flipY = false;
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.needsUpdate = true;
          resolve(texture);
        },
        undefined,
        (err) => reject(new Error(`Failed to load palette texture "${fileName}": ${String(err)}`)),
      );
    });
    paletteCache.set(fileName, pending);
  }
  return pending;
}

export async function preloadModels(names: readonly string[]): Promise<void> {
  await Promise.all([...new Set(names)].map(loadModel));
}

export interface MeshEntry {
  mesh: THREE.Mesh;
  /** Transform relative to the model's own root — most KayKit pieces are identity here, but not all (e.g. `chest_gold`'s lid). */
  localMatrix: THREE.Matrix4;
}

/**
 * Every Mesh in a loaded model's scene graph — most KayKit pieces have
 * exactly one, but some (e.g. `chest_gold`, base + lid) have more; grabbing
 * only the first silently drops geometry for those.
 */
export function allMeshes(scene: THREE.Group): MeshEntry[] {
  scene.updateMatrixWorld(true);
  const entries: MeshEntry[] = [];
  scene.traverse((child) => {
    if ((child as THREE.Mesh).isMesh) entries.push({ mesh: child as THREE.Mesh, localMatrix: child.matrixWorld.clone() });
  });
  if (entries.length === 0) throw new Error('Model scene has no Mesh');
  return entries;
}
